import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useSession } from './session-context';
import { useLanguage } from './language-context';
import { useResource } from './use-resource';
import { NavIcon } from './nav-icon';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import type { Business, Subscription } from '../mock/seeds/schema-types';

// Prefer a small variant for the rail thumbnail; fall back to whatever the server
// named the variants (ImageVariants is an open string→url map).
const THUMB_VARIANTS = ['thumb', 'small', 'sm', 'card', 'md'];

// The business's display image: its logo if it has one, else its first photo.
// Returns null when the listing has no media (⇒ lettered fallback).
function coverOf(b: Business): string | null {
  const media = b.media ?? [];
  const pick = media.find((m) => m.kind === 'logo') ?? media.find((m) => m.kind === 'photo');
  const url = pick?.url;
  if (!url) return null;
  for (const key of THUMB_VARIANTS) if (url[key]) return url[key]!;
  const first = Object.values(url)[0];
  return first ?? null;
}

// 'photo_studio' → 'Photo Studio'. A plain, tenant-blind label from the category id
// (no brand/location literals, INF-1); a full registry label can replace this later.
function categoryLabel(id: string): string {
  return id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// The active-business switcher (CSP §6 dash-merchant shell; imported design places it
// at the top of the rail as a card). A merchant account holds multiple businesses —
// one per branch (DR-10, "multiple listings" never "multi-branch") — and every
// merchant screen is scoped to the active one. Selecting a business persists via
// PATCH /v1/me/active-business (setActiveBusiness) and re-scopes the whole dashboard
// through the session epoch. The rail card shows the active business's plan · status
// line (from its subscription); the dropdown lists every business with a status pill
// and ends with an "Add a business" row. `variant` styles it for the rail, the light
// mobile bar, or (DC70) the context-bar scope pill under the top bar.
//
// `allowAll` (context bar on the screens that read `business_id=all`, session-context
// scope): the menu opens with an "All businesses" row (design M:12233) and the pill
// reads "All businesses" with the storefront glyph while that scope is on.
export function BusinessSwitcher({
  variant = 'rail',
  allowAll = false,
}: {
  variant?: 'rail' | 'bar' | 'context';
  allowAll?: boolean;
}) {
  const { businesses, activeBusiness, switchBusiness, epoch, scope, showAllBusinesses, isStaff, guardSwitch } = useSession();
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // The rail clips horizontal overflow, so its menu is positioned `fixed` from the
  // trigger's rect — it can then be full width and show the whole name + status + check
  // (the imported design), instead of being squeezed into the narrow rail.
  const [pos, setPos] = useState<{ left: number; top: number; width: number } | null>(null);

  const toggle = () => {
    setOpen((wasOpen) => {
      if (!wasOpen && variant === 'rail' && triggerRef.current) {
        const r = triggerRef.current.getBoundingClientRect();
        setPos({ left: r.left, top: r.bottom + 8, width: Math.max(r.width, 324) });
      }
      return !wasOpen;
    });
  };

  // The active business's subscription drives the "Premium · Active" plan line
  // (imported design). Best-effort — the switcher still renders if it 404s.
  const { data: subscription } = useResource<Subscription | undefined>(
    async (signal) => {
      if (!activeBusiness || isStaff) return undefined; // merchant-only (API-16)
      try {
        return await api.GET('/merchant/subscription', { signal });
      } catch {
        return undefined;
      }
    },
    [activeBusiness?.id, epoch, isStaff],
  );

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      // The rail menu is portaled outside `ref`, so check it explicitly — otherwise
      // clicking an option would register as an outside click and close before it fires.
      if (ref.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!activeBusiness) return null;
  const allOn = allowAll && scope === 'all';
  const activeId = allOn ? null : activeBusiness.id;

  const nameOf = (b: { name: string; name_bn?: string }) =>
    lang === 'bn' && b.name_bn ? b.name_bn : b.name;

  // A rounded thumbnail: the business's logo (else first photo) from media, falling
  // back to a lettered tile so a listing with no imagery still reads cleanly.
  const Thumb = ({ b }: { b: Business }) => {
    const src = coverOf(b);
    return src ? (
      <img className="biz-switcher__logo biz-switcher__logo--img" src={src} alt="" />
    ) : (
      <span className="biz-switcher__logo biz-switcher__logo--initial" aria-hidden="true">
        {b.name.charAt(0).toUpperCase()}
      </span>
    );
  };

  // "Category · Area" — the menu-row subtitle mirrors the imported design.
  const catArea = (b: Business) =>
    [categoryLabel(b.category_id), b.area_name].filter(Boolean).join(' · ');

  // "Premium · Active" — plan name from the subscription, status from a localised
  // sub_status key (falls back to the raw status if unknown, AP-D10).
  const planLine = (() => {
    if (!subscription) return activeBusiness.area_name ?? '';
    const statusKey = `merchant.sub_status.${subscription.status}` as MessageKey;
    const statusLabel = t(statusKey);
    const status = statusLabel === statusKey ? subscription.status : statusLabel;
    const plan = subscription.plan?.name_en;
    return plan ? `${plan} · ${status}` : status;
  })();

  const choose = (id: string) => {
    setOpen(false);
    // In the all-businesses scope, picking the active business still leaves that scope.
    if (id === activeBusiness.id && scope !== 'all') return;
    // A screen holding unsaved edits (a profile or turf draft) asks first: the switch
    // re-scopes it and would drop them (CB-17). Back keeps the edits and the business.
    guardSwitch(() => {
      setBusy(true);
      void switchBusiness(id)
        .catch(() => undefined)
        .finally(() => setBusy(false));
    });
  };

  const chooseAll = () => {
    setOpen(false);
    if (scope !== 'all') showAllBusinesses();
  };

  const addBusiness = () => {
    setOpen(false);
    guardSwitch(() => navigate('/business?new=1'));
  };

  const bizStatusLabel = (b: Business) => {
    const key = `merchant.sub_status.${b.status}` as MessageKey;
    const label = t(key);
    return label === key ? b.status : label;
  };

  return (
    <div className={`biz-switcher biz-switcher--${variant}`} ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        className="biz-switcher__button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('merchant.switch_business')}
        disabled={busy}
        onClick={toggle}
      >
        {allOn ? <AllGlyph /> : <Thumb b={activeBusiness} />}
        <span className="biz-switcher__text">
          <span className="biz-switcher__name">{allOn ? t('dc70.ctx.all_businesses') : nameOf(activeBusiness)}</span>
          {variant === 'rail' && planLine && (
            <span className="biz-switcher__sub">{planLine}</span>
          )}
        </span>
        <span className="biz-switcher__caret" aria-hidden="true">
          {variant === 'context' ? <NavIcon name="chevron" size={14} strokeWidth={2.4} /> : '▾'}
        </span>
      </button>

      {open && menu()}
    </div>
  );

  // The menu markup. On the rail it is portaled to <body> and positioned `fixed` from
  // the trigger, so it escapes the rail's clipping + stacking context and paints above
  // the dashboard content. On the mobile bar it stays anchored under the trigger.
  function menu() {
    const node = (
      <div
        ref={menuRef}
        className={`biz-switcher__menu${variant === 'rail' ? ' biz-switcher__menu--fixed' : ''}`}
        style={variant === 'rail' && pos ? { left: pos.left, top: pos.top, width: pos.width } : undefined}
      >
        {/* DC123: the listbox owns only the options; "Add business" is an ordinary button
            after it (axe aria-required-children). The wrapper is a plain block. */}
        <div role="listbox" aria-label={t('merchant.switch_business')}>
        {allowAll && (
          <button
            type="button"
            role="option"
            aria-selected={allOn}
            className={`biz-switcher__item${allOn ? ' is-selected' : ''}`}
            onClick={chooseAll}
          >
            <AllGlyph menu />
            <span className="biz-switcher__text">
              <span className="biz-switcher__name">{t('dc70.ctx.all_businesses')}</span>
              <span className="biz-switcher__sub">{formatMessage(lang, 'dc70.ctx.businesses_n', { n: businesses.length })}</span>
            </span>
            {allOn && (
              <span className="biz-switcher__check" aria-hidden="true">
                ✓
              </span>
            )}
          </button>
        )}
        {businesses.map((b) => {
            const active = b.id === activeId;
            return (
              <button
                key={b.id}
                type="button"
                role="option"
                aria-selected={active}
                className={`biz-switcher__item${active ? ' is-selected' : ''}`}
                onClick={() => choose(b.id)}
              >
                <Thumb b={b} />
                <span className="biz-switcher__text">
                  <span className="biz-switcher__name">{nameOf(b)}</span>
                  {catArea(b) && <span className="biz-switcher__sub">{catArea(b)}</span>}
                </span>
                <span
                  className={`biz-switcher__status${b.status === 'active' ? ' is-active' : ''}`}
                >
                  {bizStatusLabel(b)}
                </span>
                {active && (
                  <span className="biz-switcher__check" aria-hidden="true">
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>

          <button type="button" className="biz-switcher__add" onClick={addBusiness}>
            <span className="biz-switcher__add-icon" aria-hidden="true">
              +
            </span>
            <span>{t('merchant.add_business')}</span>
          </button>
      </div>
    );
    return variant === 'rail' ? createPortal(node, document.body) : node;
  }
}

// The "All businesses" glyph (design M:309-313 pill, M:326-330 menu row): a storefront.
function AllGlyph({ menu = false }: { menu?: boolean }) {
  return (
    <span className={`biz-switcher__logo biz-switcher__logo--all${menu ? ' biz-switcher__logo--all-menu' : ''}`} aria-hidden="true">
      <svg viewBox="0 0 24 24" width={menu ? 17 : 16} height={menu ? 17 : 16} fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l1.5-5h15L21 9" />
        <path d="M3 9h18v11H3z" />
        <path d="M9 20v-6h6v6" />
      </svg>
    </span>
  );
}
